import React, { useState, useEffect } from "react";
import { Company } from "../types";
import { db } from "../firebase";
import { doc, onSnapshot, getDoc } from "firebase/firestore";

type Props = {
  onSelect: (company: Company) => void;
  onClose: () => void;
};

const FoodinnovCompanySearch: React.FC<Props> = ({ onSelect, onClose }) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Company[]>([]);
  const [allCompanies, setAllCompanies] = useState<{ [key: string]: Company }>({});
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = 10;
  const totalPages = Math.ceil(results.length / pageSize);
  const paginatedResults = results.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Charger toutes les sociétés Foodinnov
  useEffect(() => {
    const ref = doc(db, "prospects", "foodinnovCompanies");
    const unsubscribe = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const data = snap.data() as { [k: string]: Company };
        // Nettoyer les enregistrements vides
        const cleanedData: { [k: string]: Company } = {};
        Object.entries(data).forEach(([key, company]) => {
          if (company && company.name && company.name.trim() !== "") {
            cleanedData[key] = company;
          }
        });
        setAllCompanies(cleanedData);
      } else {
        setAllCompanies({});
      }
    });

    return () => unsubscribe();
  }, []);

  // Filtrer les résultats en fonction de la recherche
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setCurrentPage(1);
      return;
    }

    setLoading(true);
    const searchTerm = query.toLowerCase().trim();
    
    const filtered = Object.values(allCompanies).filter((company) => {
      const name = (company.name || "").toLowerCase();
      return name.includes(searchTerm);
    });

    // Trier par nom
    filtered.sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    setResults(filtered);
    setCurrentPage(1);
    setLoading(false);
  }, [query, allCompanies]);

  return (
    <div className="insee-overlay">
      <div className="insee-box">
        <header className="insee-toolbar">
          <h3>Importer depuis Sociétés Foodinnov</h3>
          <button onClick={onClose}>✕</button>
        </header>

        <div className="insee-body">
          <input
            autoFocus
            type="text"
            placeholder="Rechercher une société par nom..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="insee-input"
          />

          {loading && <p>Recherche en cours…</p>}

          {results.length > 0 && (
            <div className="insee-results-box">
              <p>{results.length} résultat(s) trouvé(s) (page {currentPage} sur {totalPages}) :</p>
              <ul className="insee-results-list">
                {paginatedResults.map((company, index) => {
                  const location = [
                    company.postalCode,
                    company.city,
                    company.country
                  ].filter(Boolean).join(", ");

                  const principalContact = company.principalContact || company.principalFirstName
                    ? `${company.principalGender || "Mr"} ${company.principalContact || ""}${company.principalFirstName ? " " + company.principalFirstName : ""}`.trim()
                    : "Aucun contact";

                  return (
                    <li key={company.id || index} className="insee-result-item">
                      <button
                        className="insee-result-button"
                        onClick={() => onSelect(company)}
                        style={{ textAlign: "left", width: "100%" }}
                      >
                        <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                          {company.name}
                        </div>
                        {company.siret && (
                          <div style={{ fontSize: "0.9em", color: "#666" }}>
                            SIRET: {company.siret}
                          </div>
                        )}
                        {location && (
                          <div style={{ fontSize: "0.9em", color: "#666" }}>
                            {location}
                          </div>
                        )}
                        <div style={{ fontSize: "0.9em", color: "#666", marginTop: "4px" }}>
                          Contact: {principalContact}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Précédent
                  </button>
                  <span>Page {currentPage} / {totalPages}</span>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Suivant
                  </button>
                </div>
              )}
            </div>
          )}

          {!loading && results.length === 0 && query.trim() && (
            <p style={{ fontStyle: "italic" }}>Aucune société trouvée avec ce nom.</p>
          )}

          {!loading && results.length === 0 && !query.trim() && (
            <p style={{ fontStyle: "italic", color: "#666" }}>
              Saisissez un nom de société pour rechercher dans les sociétés Foodinnov.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FoodinnovCompanySearch;
